/**
 * Visionary Theme — product-form.js
 * <product-form> custom element: ATC submission, loading state, sticky ATC bar sync
 */

'use strict';

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.stickyBar = document.querySelector('.sticky-atc');

    if (!this.form) return;
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.setupStickyBar();
  }

  setupStickyBar() {
    if (!this.stickyBar) return;
    const stickyBtn = this.stickyBar.querySelector('[data-sticky-atc-btn]');
    stickyBtn?.addEventListener('click', () => this.form.requestSubmit());

    // Show sticky bar after scrolling past main ATC
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            this.stickyBar.classList.add('is-visible');
          } else {
            this.stickyBar.classList.remove('is-visible');
          }
        });
      },
      { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
    );

    if (this.submitButton) observer.observe(this.submitButton);
  }

  async onSubmit(e) {
    e.preventDefault();
    if (this.submitButton?.getAttribute('disabled')) return;

    const formData = new FormData(this.form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity'), 10) || 1;
    const properties = {};
    formData.forEach((value, key) => {
      if (key.startsWith('properties[')) {
        const propKey = key.match(/properties\[(.+)\]/)?.[1];
        if (propKey) properties[propKey] = value;
      }
    });

    this.setLoading(true);

    try {
      if (!window.CartAPI) { this.form.submit(); return; }
      const payload = { id: variantId, quantity };
      if (Object.keys(properties).length) payload.properties = properties;

      const result = await CartAPI.addItems(payload);
      const addedItem = result.items?.[0] || result;

      document.dispatchEvent(new CustomEvent('cart:added', { detail: addedItem }));
      document.dispatchEvent(new CustomEvent('cart:updated'));

      if (window.themeSettings?.redirectToCheckout) {
        window.location.href = window.themeRoutes?.checkout || '/checkout';
      } else {
        this.setSuccess();
      }
    } catch (err) {
      this.setError(err.message || 'Could not add to cart. Please try again.');
      console.error('Add to cart error:', err);
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    if (loading) {
      this.submitButton?.setAttribute('disabled', '');
      this.submitButton?.classList.add('loading');
      this.submitButton?.setAttribute('aria-label', 'Adding to cart...');
    } else {
      if (this.submitButton?.dataset.variantAvailable !== 'false') this.submitButton?.removeAttribute('disabled');
      this.submitButton?.classList.remove('loading');
    }
  }

  setSuccess() {
    const originalText = this.submitButton?.dataset.originalText || 'Add to Cart';
    const successText = this.submitButton?.dataset.successText || 'Added!';

    this.submitButton?.classList.add('success');
    if (this.submitButton) {
      const textEl = this.submitButton.querySelector('[data-btn-text]') || this.submitButton;
      textEl.textContent = successText;
    }

    setTimeout(() => {
      this.submitButton?.classList.remove('success');
      const textEl = this.submitButton?.querySelector('[data-btn-text]') || this.submitButton;
      if (textEl) textEl.textContent = originalText;
    }, 2500);
  }

  setError(message) {
    let errorEl = this.querySelector('.product-form__error');
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'product-form__error';
      errorEl.setAttribute('role', 'alert');
      this.form.insertAdjacentElement('afterend', errorEl);
    }
    errorEl.textContent = message;
    errorEl.removeAttribute('hidden');

    setTimeout(() => errorEl.setAttribute('hidden', ''), 5000);
  }
}

if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
